import {Component, EventEmitter, Inject, Input, OnInit, Optional, Output} from '@angular/core';
import {of, Subject} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {CommonConfig} from '@ui-app/configs/common.config';
import {IDownloadUrlResponse, IUploadUrlRequest, PhotoData, UPLOAD_URL} from '@ui-app/models/common.models';
import {BaseComponent} from '../../base.component';
import {PhotoUploadService} from '../camera/photo-upload.service';
import {AppToastrService} from '@ui-app/services/toastr.service';
import {ImageService} from '@ui-app/services/image.service';
import {S3DownloadUrlService} from '@ui-app/services/s3-download-url.service';

@Component({
    selector: 'ui-mobile-camera-and-preview',
    templateUrl: './mobile-camera-and-preview.component.html',
    styleUrls: ['./mobile-camera-and-preview.component.less'],
    providers: [ImageService]
})
export class MobileCameraAndPreviewComponent extends BaseComponent implements OnInit {
    @Input() width: number = 300;
    @Input() height: number = 300;
    @Input() defaultConfig = CommonConfig.cameraConfig;
    @Input() dataUrl: string | ArrayBuffer;
    @Input() newFileName: string;
    @Input() canUseCurrentImage: boolean = false;
    @Input() photoData: PhotoData;

    @Output() onPhotoTaken = new EventEmitter<PhotoData>();
    @Output() onDeletePhoto = new EventEmitter<void>();
    @Output() onUseCurrentPhoto = new EventEmitter<void>();

    imageType: string = this.defaultConfig.defaultImageType;
    imageData: ImageData;
    uploadInProgress: boolean;
    currentPhotoLoaded: boolean = false;
    selectedPhotoFileLength: number;

    saveAction$ = new Subject<IUploadUrlRequest>();

    constructor(
        private toastrSevice: AppToastrService,
        private imageService: ImageService,
        private downloadUrlService: S3DownloadUrlService,
        @Optional() @Inject(UPLOAD_URL) private uploadUrl: string,
        @Optional() @Inject(PhotoUploadService) private photoUploadService: PhotoUploadService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.handleActions();
    }

    private handleActions(): void {
        /**
         * save photo
         */
        let photoData;
        let downloadUrlResponse;
        this.subs = this.saveAction$
            .pipe(
                switchMap((request) => this.photoUploadService.uploadPhoto(request, this.uploadUrl, this.dataUrl as string)),
                tap((photo: PhotoData) => photoData = photo),
                switchMap((photo) => this.downloadUrlService.getUrl(photo.uId)),
                tap((urlResponse: IDownloadUrlResponse) => downloadUrlResponse = urlResponse),
                switchMap(() => of({...photoData, ...downloadUrlResponse})),
            )
            .subscribe((data: PhotoData) => {
                this.onPhotoTaken.emit(data);
                this.photoData = data;
                this.uploadInProgress = false;
            },
            (error) => {
                this.toastrSevice.error('Something went wrong. Please refresh the page and try again', null);
                this.uploadInProgress = false;
            });
    }

    onPhotoSelected(event: Event & { target: HTMLInputElement }): void {
        const renamedFile = this.imageService.changeFileName(event.target.files[0], this.newFileName);
        const reader = new FileReader();
        this.selectedPhotoFileLength = renamedFile.size;

        if (renamedFile.size > this.defaultConfig.maxPhotoFileSize) {
            this.toastrSevice.warning('File size is exceeded. Max size is 10 Mb');
            return;
        }
        reader.onloadend = () => {
            this.dataUrl = reader.result;
        };
        reader.readAsDataURL(renamedFile);
    }

    onRemoveSelectedPhoto(): void {
        this.currentPhotoLoaded = false;
        this.dataUrl = null;
        this.photoData = null;
        this.onDeletePhoto.emit();
    }

    onConfirmSelectedPhoto(): void {
        this.uploadInProgress = true;
        const request: IUploadUrlRequest = {
            name: `${this.newFileName}.${this.imageType.split('/')[1]}` || `${Date.now()}.${this.imageType.split('/')[1]}`,
            contentType: this.imageType,
            length: this.selectedPhotoFileLength
        };

        this.saveAction$.next(request);
    }

    useCurrentPhoto(): void {
        this.currentPhotoLoaded = true;
        this.onUseCurrentPhoto.emit();
    }
}
